import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../constants/firebase';
import { Card, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { createLogger } from '../../utils/logger';

const logger = createLogger('FCMStatusChecker');

export default function FCMStatusChecker() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resettingTokens, setResettingTokens] = useState(new Set());
  const [message, setMessage] = useState({ type: '', text: '' });

  const fetchUsersWithFCMTokens = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get all users
      const usersSnapshot = await getDocs(collection(db, 'Users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setUsers(usersData);
    } catch (err) {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsersWithFCMTokens();
  }, []);

  const resetFCMToken = async (userId, userName) => {
    try {
      setResettingTokens(prev => new Set(prev).add(userId));
      setMessage({ type: '', text: '' });
      
      const userRef = doc(db, 'Users', userId);
      await updateDoc(userRef, {
        fcmToken: null,
        fcmTokenInvalidatedAt: serverTimestamp()
      });
      
      logger.info(`[FCMStatusChecker] Reset FCM token for user: ${userName}`);
      setMessage({ type: 'success', text: `FCM token reset for ${userName}` });
      
      // Refresh the user list
      await fetchUsersWithFCMTokens();
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      logger.error(`[FCMStatusChecker] Error resetting FCM token for user ${userName}:`, err);
      setMessage({ type: 'danger', text: `Failed to reset FCM token for ${userName}: ${err.message}` });
      
      // Clear error message after 5 seconds
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setResettingTokens(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const usersWithFCM = users.filter(user => user.fcmToken);
  const usersWithoutFCM = users.filter(user => !user.fcmToken);

  if (loading) {
    return (
      <Card>
        <Card.Body className="text-center">
          <Spinner animation="border" />
          <p className="mt-2">Loading FCM status...</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header>
        <h5>FCM Token Status</h5>
        <Button variant="outline-primary" size="sm" onClick={fetchUsersWithFCMTokens}>
          Refresh
        </Button>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger">{error}</Alert>
        )}
        
        {message.text && (
          <Alert variant={message.type} dismissible onClose={() => setMessage({ type: '', text: '' })}>
            {message.text}
          </Alert>
        )}
        
        <div className="row mb-3">
          <div className="col-md-6">
            <h6>
              Users with FCM Tokens 
              <Badge bg="success" className="ms-2">{usersWithFCM.length}</Badge>
            </h6>
          </div>
          <div className="col-md-6">
            <h6>
              Users without FCM Tokens 
              <Badge bg="warning" className="ms-2">{usersWithoutFCM.length}</Badge>
            </h6>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <h6>✅ Users Ready for Notifications:</h6>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {usersWithFCM.map(user => {
                const userName = user.first_name || user.displayName || user.email || user.identity_id || 'Unknown User';
                const isResetting = resettingTokens.has(user.id);
                
                return (
                  <div key={user.id} className="border-bottom py-2">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <small>
                          <strong>{userName}</strong>
                          <br />
                          <span className="text-muted">
                            {user.email && user.email !== userName ? `(${user.email})` : ''}
                          </span>
                          <br />
                          <code className="text-muted">{user.fcmToken?.substring(0, 20)}...</code>
                        </small>
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => resetFCMToken(user.id, userName)}
                        disabled={isResetting}
                        className="ms-2"
                        title="Reset FCM Token"
                      >
                        {isResetting ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <i className="fas fa-redo"></i>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="col-md-6">
            <h6>⚠️ Users Not Ready:</h6>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {usersWithoutFCM.map(user => (
                <div key={user.id} className="border-bottom py-1">
                  <small>
                    <strong>
                      {user.first_name || user.displayName || user.email || user.identity_id || 'Unknown User'}
                    </strong>
                    <br />
                    <span className="text-muted">
                      {user.email && user.email !== user.first_name ? `(${user.email})` : ''}
                    </span>
                    <br />
                    <span className="text-muted">No FCM token</span>
                  </small>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-3">
          <small className="text-muted">
            💡 <strong>To generate FCM message data:</strong><br />
            1. Have users login and allow notifications<br />
            2. Place orders as customers<br />
            3. Pack orders as employees<br />
            4. Check Firebase Console &gt; Messaging for message statistics
          </small>
        </div>
      </Card.Body>
    </Card>
  );
}
